import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { EngineeringSupportBanner } from '../../components/overlays/EngineeringSupportBanner';

type AuthTransitionScreenProps = {
  title?: string;
  description?: string;
  testId?: string;
};

export const AuthTransitionScreen = ({
  title = 'Preparing access',
  description = 'Waiting for authentication state to settle.',
  testId = 'auth-transition-screen',
}: AuthTransitionScreenProps) => (
  <section
    data-testid={testId}
    className="min-h-screen bg-background-light px-6 py-16 text-irongrey"
  >
    <div className="mx-auto max-w-xl rounded-3xl border border-gray-100 bg-white px-6 py-8 shadow-sm">
      <div className="mb-4 flex items-center gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
        <div>
          <h1 className="text-xl font-bold">{title}</h1>
          <p className="mt-1 text-sm text-irongrey/60">{description}</p>
        </div>
      </div>
    </div>
  </section>
);

type AuthFailureScreenProps = {
  title?: string;
  description?: string;
  testId?: string;
  actions?: ReactNode;
  diagnostics?: ReactNode;
};

export const AuthFailureScreen = ({
  title = 'Unable to finish sign in',
  description = 'This session authenticated, but YSTNJ could not finish preparing your access.',
  testId = 'auth-failure-screen',
  actions = null,
  diagnostics = null,
}: AuthFailureScreenProps) => (
  <section
    data-testid={testId}
    className="min-h-screen bg-background-light px-6 py-16 text-irongrey"
  >
    <div className="mx-auto max-w-xl rounded-3xl border border-rose-100 bg-white px-6 py-8 shadow-sm">
      <EngineeringSupportBanner className="mb-5" />
      <div className="mb-4 flex items-start gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-rose-50 text-rose-600">
          <AlertTriangle size={20} />
        </span>
        <div className="min-w-0">
          <h1 className="text-xl font-bold">{title}</h1>
          <p className="mt-1 text-sm text-irongrey/70">{description}</p>
        </div>
      </div>
      {actions ? (
        <div className="mt-6 flex flex-wrap gap-3">
          {actions}
        </div>
      ) : null}
      {diagnostics ? (
        <div className="mt-6">
          {diagnostics}
        </div>
      ) : null}
    </div>
  </section>
);
